/**
 * TanStack Query hooks for the conversations inbox and the Customer 360 conversation tab.
 *
 * Polling: the queue and the open detail refetch every `POLL_INTERVAL_MS` (≤ 10 s, the design's
 * ceiling) and only while the tab is visible (`refetchIntervalInBackground` stays false).
 *
 * The timeline is two queries (design §11.6): `useConversationLatestMessages` — the live tail, the
 * newest window polled every `TIMELINE_POLL_INTERVAL_MS`; and `useConversationMessages` — the keyset
 * infinite query of OLDER pages (`nextBefore` is the cursor, «Load earlier messages» calls
 * `fetchNextPage()`), `staleTime: Infinity` because history does not change. Keeping them separate
 * avoids TanStack v5's sequential refetch of every loaded page on each interval. `mergeTimeline`
 * joins them for rendering.
 *
 * Mutations invalidate the conversation's detail, timeline and episodes plus every list, because
 * every action moves the episode (status, version, `lastActivityAt`) and therefore the queue.
 */
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type {
  UseInfiniteQueryResult,
  UseMutationResult,
  UseQueryResult,
  InfiniteData,
} from '@tanstack/react-query';

import type { NormalizedApiError } from '@/shared/api';

import {
  closeConversation,
  getConversation,
  getCustomerConversation,
  listConversationEpisodes,
  listConversationMessages,
  listConversations,
  patchConversationEpisode,
  patchEscalation,
  postOperatorReply,
} from '../api/conversationsApi';
import type {
  CloseConversationRequest,
  ConversationChannel,
  ConversationDetail,
  ConversationEpisodePage,
  ConversationEpisodeSummary,
  ConversationEscalation,
  ConversationMessagePage,
  ConversationMessageView,
  ConversationPage,
  ListConversationEpisodesParams,
  ListConversationsParams,
  OperatorReplyAccepted,
  OperatorReplyRequest,
  PatchConversationEpisodeRequest,
  PatchEscalationRequest,
} from '../model/types';
import { conversationsKeys } from './queryKeys';

/** The design's polling ceiling for the queue and the open conversation. */
export const POLL_INTERVAL_MS = 10_000;

/** The live tail of an open conversation polls faster than the queue (design §11.6). */
export const TIMELINE_POLL_INTERVAL_MS = 5_000;

/** The timeline page size requested per keyset window (contract max 200, default 50). */
export const MESSAGES_PAGE_SIZE = 50;

interface QueryOptions {
  enabled?: boolean;
  /** `false` disables polling (e.g. a read-only embed such as the Customer 360 tab). */
  poll?: boolean;
}

export function useConversations(
  params: ListConversationsParams,
  options?: QueryOptions,
): UseQueryResult<ConversationPage, NormalizedApiError> {
  return useQuery<ConversationPage, NormalizedApiError>({
    queryKey: conversationsKeys.list(params),
    queryFn: () => listConversations(params),
    enabled: options?.enabled ?? true,
    placeholderData: keepPreviousData,
    refetchInterval: (options?.poll ?? true) ? POLL_INTERVAL_MS : false,
  });
}

/**
 * The conversation header. A MERGED alias answers with the SURVIVING thread (`data.id !== conversationId`);
 * the page is responsible for following it (adr-merchant-portal-021 D2) — see `mergedAliasTarget`.
 */
export function useConversation(
  conversationId: string | null,
  options?: QueryOptions,
): UseQueryResult<ConversationDetail, NormalizedApiError> {
  return useQuery<ConversationDetail, NormalizedApiError>({
    queryKey: conversationsKeys.detail(conversationId ?? ''),
    queryFn: () => getConversation(conversationId as string),
    enabled: Boolean(conversationId) && (options?.enabled ?? true),
    refetchInterval: (options?.poll ?? true) ? POLL_INTERVAL_MS : false,
  });
}

/**
 * The id the page must navigate to when the requested conversation is a MERGED alias, else `null`.
 * Prefers the hub's own `mergedInto` (design §13.5, adr-merchant-portal-022 D3/D6) when the BFF sends
 * it; falls back to comparing the requested id with `detail.id` — kept for an older BFF that has not
 * shipped the pass-through yet, and because it is a harmless no-op once `mergedInto` is always present.
 */
export function mergedAliasTarget(
  requestedId: string,
  detail: ConversationDetail | undefined,
): string | null {
  if (!detail) {
    return null;
  }
  if (detail.mergedInto && detail.mergedInto !== requestedId) {
    return detail.mergedInto;
  }
  if (detail.id !== requestedId) {
    return detail.id;
  }
  return null;
}

/**
 * The live tail: the newest `MESSAGES_PAGE_SIZE` messages, polled. Its `nextBefore` seeds the
 * older-pages query when the operator asks for earlier messages.
 */
export function useConversationLatestMessages(
  conversationId: string | null,
  options?: QueryOptions,
): UseQueryResult<ConversationMessagePage, NormalizedApiError> {
  return useQuery<ConversationMessagePage, NormalizedApiError>({
    queryKey: conversationsKeys.messagesLatest(conversationId ?? ''),
    queryFn: () => listConversationMessages(conversationId as string, { size: MESSAGES_PAGE_SIZE }),
    enabled: Boolean(conversationId) && (options?.enabled ?? true),
    refetchInterval: (options?.poll ?? true) ? TIMELINE_POLL_INTERVAL_MS : false,
  });
}

/**
 * The OLDER pages of the timeline, keyset-paged backwards from `startBefore` (the live tail's
 * `nextBefore`, part of the query key — see `conversationsKeys.messages`). Never polled — history
 * does not change; an explicit Reload or a remount refetches. Disabled until the tail has a cursor
 * (i.e. there IS something older than the first window).
 */
export function useConversationMessages(
  conversationId: string | null,
  startBefore: string | null | undefined,
  options?: QueryOptions,
): UseInfiniteQueryResult<
  InfiniteData<ConversationMessagePage, string | undefined>,
  NormalizedApiError
> {
  return useInfiniteQuery<
    ConversationMessagePage,
    NormalizedApiError,
    InfiniteData<ConversationMessagePage, string | undefined>,
    ReturnType<typeof conversationsKeys.messages>,
    string | undefined
  >({
    queryKey: conversationsKeys.messages(conversationId ?? '', startBefore ?? ''),
    queryFn: ({ pageParam }) =>
      listConversationMessages(conversationId as string, {
        before: pageParam ?? (startBefore as string),
        size: MESSAGES_PAGE_SIZE,
      }),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.nextBefore ?? undefined,
    enabled: Boolean(conversationId) && Boolean(startBefore) && (options?.enabled ?? true),
    staleTime: Infinity,
  });
}

/**
 * Joins the live tail and the older pages into ONE chronological list (oldest first, newest last —
 * the order a chat renders in). A row present in both (a boundary message, or one re-delivered by a
 * poll) is taken from the TAIL, whose `deliveryStatus`/`edited`/`deleted` are fresher. The wire order
 * inside a page is not fixed by the contract (design §12 G13), so the result is sorted by `createdAt`
 * (then `id` for a stable tie-break) rather than trusting it.
 */
export function mergeTimeline(
  older: InfiniteData<ConversationMessagePage, string | undefined> | undefined,
  latest: ConversationMessagePage | undefined,
) {
  const byId = new Map<string, ConversationMessagePage['content'][number]>();
  for (const page of older?.pages ?? []) {
    for (const message of page.content) {
      byId.set(message.id, message);
    }
  }
  for (const message of latest?.content ?? []) {
    byId.set(message.id, message);
  }
  return [...byId.values()].sort(
    (a, b) => a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id),
  );
}

export function useConversationEpisodes(
  conversationId: string | null,
  params: ListConversationEpisodesParams = {},
  options?: QueryOptions,
): UseQueryResult<ConversationEpisodePage, NormalizedApiError> {
  return useQuery<ConversationEpisodePage, NormalizedApiError>({
    queryKey: conversationsKeys.episodes(conversationId ?? '', params),
    queryFn: () => listConversationEpisodes(conversationId as string, params),
    enabled: Boolean(conversationId) && (options?.enabled ?? true),
    placeholderData: keepPreviousData,
  });
}

/**
 * Customer 360's shortcut. A `404` means «never talked» — the tab's EMPTY state, not an error — so it
 * is exposed as `notFound` and never retried.
 */
export function useCustomerConversation(
  customerId: string | null,
  options?: QueryOptions,
): UseQueryResult<ConversationDetail, NormalizedApiError> & { notFound: boolean } {
  const query = useQuery<ConversationDetail, NormalizedApiError>({
    queryKey: conversationsKeys.customerConversation(customerId ?? ''),
    queryFn: () => getCustomerConversation(customerId as string),
    enabled: Boolean(customerId) && (options?.enabled ?? true),
    retry: (failureCount, error) => error.status !== 404 && failureCount < 1,
    refetchInterval: (options?.poll ?? false) ? POLL_INTERVAL_MS : false,
  });
  return Object.assign(query, { notFound: query.isError && query.error?.status === 404 });
}

// ── Mutations ────────────────────────────────────────────────────────────────

function useInvalidateConversation(conversationId: string) {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: conversationsKeys.detail(conversationId) }),
      queryClient.invalidateQueries({ queryKey: conversationsKeys.messagesLatest(conversationId) }),
      queryClient.invalidateQueries({
        queryKey: [...conversationsKeys.root, 'messages', conversationId],
      }),
      queryClient.invalidateQueries({
        queryKey: [...conversationsKeys.root, 'episodes', conversationId],
      }),
      queryClient.invalidateQueries({ queryKey: conversationsKeys.lists() }),
      queryClient.invalidateQueries({ queryKey: [...conversationsKeys.root, 'customer'] }),
    ]);
  };
}

export interface PostOperatorReplyOptions {
  /**
   * Fallback channel for the optimistic tail row when the request omits one (`OperatorReplyRequest.channel`
   * defaults server-side to the channel of the customer's LAST INBOUND message, design §13.4) — the
   * composer passes its own `lastChannel` prop, which already IS that value.
   */
  fallbackChannel?: ConversationChannel;
  /**
   * The signed-in operator — stamps the optimistic row's `operatorActorId`/`operator` (design §13.3) so
   * it reads "Operator · you" at once, before the tail poll replaces it by id with the server's own
   * resolved ref. `null`/absent renders the same as an older BFF with no ref at all — plain "Operator".
   */
  me?: { id: string; name?: string | null } | null;
}

/**
 * Posting an operator reply.
 *
 * **Ordering (A5 fix, WP5 review).** TanStack Query v5 always resolves a mutation's HOOK-level
 * `onSuccess` before the CALL-SITE one passed to `mutate(body, { onSuccess })` — so the composer used
 * to invalidate the whole conversation (via this hook's old `onSuccess`) BEFORE its own call-site
 * `onSuccess` inserted the optimistic `QUEUED` bubble into the tail cache. Because the accepted
 * message is not guaranteed to be back on a re-`GET` yet (the hub's read model settles slightly after
 * the `202`), that ordering could refetch a tail that still does not contain the reply.
 *
 * The fix is to do the optimistic insert and the invalidation from the SAME place (here, the
 * hook-level `onSuccess`, which genuinely does run first) rather than splitting them across the hook
 * and the call site: (1) insert the `QUEUED` row into `messagesLatest` first; (2) invalidate every
 * OTHER surface an accepted reply moves — detail (status/version), the lists (queue membership),
 * episodes, the Customer 360 shortcut. `messagesLatest` and the older keyset pages are deliberately
 * NOT invalidated here: the 5 s poll reconciles the tail once the real row lands, and invalidating it
 * immediately would risk overwriting the very row this `onSuccess` just inserted with a still-stale
 * server response. A reply that also closes the conversation is the one exception — closing is
 * expected to change the tail (e.g. a closing notice), so `messagesLatest` IS invalidated then.
 *
 * **The MERGED alias (design §13.5).** `OperatorReplyAccepted.conversationId` names the thread the
 * reply actually landed in — the SURVIVOR when the requested id was a MERGED alias (the hub follows
 * `mergedInto` for every conversation-scoped action). `targetId` below is that id when present, else
 * the requested `conversationId` unchanged; the optimistic row and the tail invalidation always use
 * `targetId` (writing it under the alias's own cache key would leave it stranded once the page
 * redirects — the survivor's timeline is what actually renders). Invalidating the ALIAS's own detail
 * query too (when it differs) is what makes `ConversationDetailPage`'s existing merge-redirect effect
 * fire almost immediately, with no separate navigation call needed here: the invalidated `GET
 * /conversations/{aliasId}` refetches right away and now answers with the survivor's own detail.
 *
 * The composer's own `mutate(...)`-level `onSuccess` is now limited to component-local state (clear
 * the draft, mint the next `replyId`, show the toast) — there is exactly one place that writes to the
 * query cache.
 */
export function usePostOperatorReply(
  conversationId: string,
  options?: PostOperatorReplyOptions,
): UseMutationResult<OperatorReplyAccepted, NormalizedApiError, OperatorReplyRequest> {
  const queryClient = useQueryClient();
  return useMutation<OperatorReplyAccepted, NormalizedApiError, OperatorReplyRequest>({
    mutationFn: (body) => postOperatorReply(conversationId, body),
    onSuccess: async (accepted, variables) => {
      const targetId = accepted.conversationId ?? conversationId;
      const detail = queryClient.getQueryData<ConversationDetail>(
        conversationsKeys.detail(targetId),
      );
      // `detail?.lastChannel` is the last-resort fallback: `ConversationSummary.lastChannel` is a
      // required field, so a loaded detail always names one, and the composer only ever renders once
      // the detail (and therefore its `lastChannel` prop) is loaded.
      const channel: ConversationChannel =
        variables.channel ?? options?.fallbackChannel ?? detail?.lastChannel ?? 'TELEGRAM';
      const operatorActorId = options?.me?.id ?? null;
      const optimisticRow: ConversationMessageView = {
        id: accepted.messageId,
        direction: 'OUT',
        kind: 'OPERATOR',
        handledBy: 'OPERATOR',
        channel,
        deliveryStatus: 'QUEUED',
        createdAt: new Date().toISOString(),
        text: variables.parts.find((part) => part.type === 'TEXT')?.text ?? null,
        parts: variables.parts,
        actions: variables.actions,
        replyToMessageId: variables.replyToMessageId ?? null,
        operatorActorId,
        operator: operatorActorId
          ? {
              actorId: operatorActorId,
              displayName: options?.me?.name ?? null,
              resolution: 'MEMBER',
            }
          : null,
      };
      queryClient.setQueryData<ConversationMessagePage>(
        conversationsKeys.messagesLatest(targetId),
        (prev) => (prev ? { ...prev, content: [optimisticRow, ...prev.content] } : prev),
      );

      const detailIds = targetId === conversationId ? [conversationId] : [conversationId, targetId];
      const invalidations = detailIds.flatMap((id) => [
        queryClient.invalidateQueries({ queryKey: conversationsKeys.detail(id) }),
        queryClient.invalidateQueries({ queryKey: [...conversationsKeys.root, 'episodes', id] }),
      ]);
      invalidations.push(
        queryClient.invalidateQueries({ queryKey: conversationsKeys.lists() }),
        queryClient.invalidateQueries({ queryKey: [...conversationsKeys.root, 'customer'] }),
      );
      if (variables.closeConversation) {
        invalidations.push(
          queryClient.invalidateQueries({ queryKey: conversationsKeys.messagesLatest(targetId) }),
        );
      }
      await Promise.all(invalidations);
    },
  });
}

export function usePatchEscalation(
  conversationId: string,
): UseMutationResult<
  ConversationEscalation,
  NormalizedApiError,
  { escalationId: string; body: PatchEscalationRequest }
> {
  const invalidate = useInvalidateConversation(conversationId);
  return useMutation<
    ConversationEscalation,
    NormalizedApiError,
    { escalationId: string; body: PatchEscalationRequest }
  >({
    mutationFn: ({ escalationId, body }) => patchEscalation(conversationId, escalationId, body),
    onSuccess: invalidate,
  });
}

export function useCloseConversation(
  conversationId: string,
): UseMutationResult<ConversationDetail, NormalizedApiError, CloseConversationRequest | undefined> {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateConversation(conversationId);
  return useMutation<ConversationDetail, NormalizedApiError, CloseConversationRequest | undefined>({
    mutationFn: (body) => closeConversation(conversationId, body),
    onSuccess: async (detail) => {
      // The response IS the new detail (incl. the closed episode's state) — seed it, then refresh the rest.
      queryClient.setQueryData(conversationsKeys.detail(conversationId), detail);
      await invalidate();
    },
  });
}

export function usePatchConversationEpisode(
  conversationId: string,
): UseMutationResult<
  ConversationEpisodeSummary,
  NormalizedApiError,
  { episodeId: string; body: PatchConversationEpisodeRequest }
> {
  const invalidate = useInvalidateConversation(conversationId);
  return useMutation<
    ConversationEpisodeSummary,
    NormalizedApiError,
    { episodeId: string; body: PatchConversationEpisodeRequest }
  >({
    mutationFn: ({ episodeId, body }) => patchConversationEpisode(conversationId, episodeId, body),
    onSuccess: invalidate,
  });
}

export { conversationsKeys } from './queryKeys';
